<mat-expansion-panel
  class="!overflow-hidden !rounded-2xl !border !border-gray-6 !bg-surface-default !shadow-sm transition-all duration-200 hover:!border-gray-7 !before:hidden relative"
  [expanded]="expanded()"
  (expandedChange)="expanded.set($event)"
>
  <!-- Subtle decorative ambient glow -->
  <div
    class="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full blur-3xl"
    [class.bg-jade-a2]="totalDetectedCount() === 0"
    [class.bg-amber-a2]="totalDetectedCount() > 0"
  ></div>

  <!-- Card Header -->
  <mat-expansion-panel-header
    class="!h-auto !py-4.5 !px-6 hover:!bg-gray-2/40 transition-colors [&_.mat-content]:!flex-row [&_.mat-content]:!items-center [&_.mat-content]:!justify-between [&_.mat-content]:!w-full [&_.mat-content]:!m-0 [&_.mat-content]:!overflow-visible"
  >
    <div class="flex items-center justify-between w-full gap-4 mr-3">
      <div class="flex items-center gap-3.5 min-w-0">
        <div
          class="h-11 w-11 flex items-center justify-center rounded-xl border shadow-xs shrink-0"
          [class.bg-jade-a2]="totalDetectedCount() === 0"
          [class.text-jade-11]="totalDetectedCount() === 0"
          [class.border-jade-a4]="totalDetectedCount() === 0"
          [class.bg-amber-a2]="totalDetectedCount() > 0"
          [class.text-amber-11]="totalDetectedCount() > 0"
          [class.border-amber-a4]="totalDetectedCount() > 0"
        >
          <mat-icon class="text-2xl leading-none"> shield </mat-icon>
        </div>
        <div class="min-w-0">
          <div class="flex items-center gap-2">
            <h4 class="text-base sm:text-lg font-bold text-gray-12 m-0">Géorisques</h4>
          </div>
          <p class="text-xs sm:text-sm text-gray-11 m-0 mt-0.5 truncate hidden sm:block">
            Exposition aux aléas naturels et technologiques recensés par l'État.
          </p>
        </div>
      </div>

      <div class="shrink-0">
        @if (geohazard()) {
          @if (totalDetectedCount() === 0) {
            <div
              class="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold px-3 py-1.5 rounded-full border border-jade-a4 bg-jade-a2 text-jade-11 shadow-xs"
            >
              <mat-icon fontIcon="check_circle" class="text-sm leading-none shrink-0"></mat-icon>
              <span>Aucun risque majeur recensé</span>
            </div>
          } @else {
            <div
              class="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold px-3 py-1.5 rounded-full border border-amber-a4 bg-amber-a2 text-amber-11 shadow-xs"
            >
              <span>{{ detectedRisksLabel() }}</span>
            </div>
          }
        } @else {
          <div
            class="inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium px-3 py-1 rounded-full border border-gray-6 bg-gray-2 text-gray-11 shadow-2xs"
          >
            <mat-icon fontIcon="hourglass_empty" class="text-xs leading-none shrink-0"></mat-icon>
            <span>Analyse en cours</span>
          </div>
        }
      </div>
    </div>
  </mat-expansion-panel-header>

  <div class="px-6 pb-6 pt-3 border-t border-gray-5">
    @if (geohazard(); as data) {
      <!-- Detected Risks Alert (if any) -->
      @if (totalDetectedCount() > 0) {
        <div class="mt-5 rounded-xl border border-amber-a4 bg-amber-a2/40 p-4">
          <div class="flex items-center gap-2 mb-3">
            <span class="text-sm font-bold uppercase tracking-wider text-amber-11">
              Aléas déclarés sur la parcelle
            </span>
          </div>

          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
            @for (risk of detectedNaturalRisks(); track risk.key) {
              <div
                class="flex items-start gap-3 rounded-lg border border-amber-a4/60 bg-surface-default p-3 shadow-2xs"
              >
                <div
                  class="h-9 w-9 rounded-lg bg-amber-a2 text-amber-11 flex items-center justify-center shrink-0 border border-amber-a4"
                >
                  <mat-icon [fontIcon]="risk.icon" class="text-lg leading-none"></mat-icon>
                </div>
                <div class="flex flex-col min-w-0">
                  <div class="flex items-center gap-2">
                    <span class="text-sm font-bold text-gray-12">{{ risk.libelle }}</span>
                    <span
                      class="text-xs font-semibold text-amber-11 bg-amber-a2 px-2 py-0.5 rounded border border-amber-a4"
                      >Présent</span
                    >
                  </div>
                  @if (risk.precision) {
                    <span class="text-xs sm:text-sm text-gray-11 mt-1 leading-snug">{{
                      risk.precision
                    }}</span>
                  }
                </div>
              </div>
            }

            @for (risk of detectedTechnoRisks(); track risk.key) {
              <div
                class="flex items-start gap-3 rounded-lg border border-amber-a4/60 bg-surface-default p-3 shadow-2xs"
              >
                <div
                  class="h-9 w-9 rounded-lg bg-ruby-a2 text-ruby-11 flex items-center justify-center shrink-0 border border-ruby-a4"
                >
                  <mat-icon [fontIcon]="risk.icon" class="text-lg leading-none"></mat-icon>
                </div>
                <div class="flex flex-col min-w-0">
                  <div class="flex items-center gap-2">
                    <span class="text-sm font-bold text-gray-12">{{ risk.libelle }}</span>
                    <span
                      class="text-xs font-semibold text-amber-11 bg-amber-a2 px-2 py-0.5 rounded border border-amber-a4"
                      >Présent</span
                    >
                  </div>
                  @if (risk.precision) {
                    <span class="text-xs sm:text-sm text-gray-11 mt-1 leading-snug">{{
                      risk.precision
                    }}</span>
                  }
                </div>
              </div>
            }
          </div>
        </div>
      }

      <!-- 2 Column Breakdown -->
      <div class="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <!-- Section A: Risques naturels -->
        <div class="flex flex-col gap-3 rounded-2xl border border-gray-6 bg-surface-default p-5 shadow-xs">
          <div class="flex items-center justify-between pb-3 border-b border-gray-5">
            <div class="flex items-center gap-2.5">
              <div
                class="h-8 w-8 rounded-lg bg-indigo-a2 text-indigo-11 flex items-center justify-center shrink-0 border border-indigo-a4"
              >
                <mat-icon fontIcon="nature" class="text-base leading-none shrink-0"></mat-icon>
              </div>
              <div>
                <h5 class="text-sm font-bold text-gray-12 m-0">Risques naturels</h5>
                <p class="text-xs text-gray-11 m-0">Aléas climatiques et mouvements de sol</p>
              </div>
            </div>
            <span
              class="text-xs font-semibold px-2.5 py-1 rounded-full border shrink-0"
              [class.bg-amber-a2]="detectedNaturalRisks().length > 0"
              [class.text-amber-11]="detectedNaturalRisks().length > 0"
              [class.border-amber-a4]="detectedNaturalRisks().length > 0"
              [class.bg-jade-a2]="detectedNaturalRisks().length === 0"
              [class.text-jade-11]="detectedNaturalRisks().length === 0"
              [class.border-jade-a4]="detectedNaturalRisks().length === 0"
            >
              {{ naturalRisksCountLabel() }}
            </span>
          </div>

          <div
            class="divide-y divide-gray-5/60 rounded-xl border border-gray-5/80 bg-gray-1/50 overflow-hidden"
          >
            @for (risk of naturalRiskItems(); track risk.key) {
              <div
                class="flex items-center justify-between px-3.5 py-2.5 text-sm transition-colors"
                [class.bg-amber-a1/60]="risk.present"
                [class.hover:bg-gray-2]="!risk.present"
                [class.hover:bg-amber-a1]="risk.present"
              >
                <div class="flex items-center gap-3 min-w-0 pr-2">
                  <div
                    class="h-7 w-7 rounded-md flex items-center justify-center shrink-0"
                    [class.bg-amber-a2]="risk.present"
                    [class.text-amber-11]="risk.present"
                    [class.border]="risk.present"
                    [class.border-amber-a4]="risk.present"
                    [class.bg-gray-3]="!risk.present"
                    [class.text-gray-9]="!risk.present"
                  >
                    <mat-icon [fontIcon]="risk.icon" class="text-sm leading-none shrink-0"></mat-icon>
                  </div>
                  <span
                    class="text-sm"
                    [class.font-semibold]="risk.present"
                    [class.text-gray-12]="risk.present"
                    [class.text-gray-11]="!risk.present"
                  >
                    {{ risk.libelle }}
                  </span>
                </div>
                @if (risk.present) {
                  <span
                    class="inline-flex items-center gap-1 rounded-md bg-amber-a2 px-2.5 py-0.5 text-xs font-semibold text-amber-11 border border-amber-a4 shrink-0 shadow-2xs"
                  >
                    Présent
                  </span>
                } @else {
                  <span class="text-xs text-gray-9 shrink-0 flex items-center gap-1 font-medium">
                    Non exposé
                  </span>
                }
              </div>
            }
          </div>
        </div>

        <!-- Section B: Risques technologiques -->
        <div class="flex flex-col gap-3 rounded-2xl border border-gray-6 bg-surface-default p-5 shadow-xs">
          <div class="flex items-center justify-between pb-3 border-b border-gray-5">
            <div class="flex items-center gap-2.5">
              <div
                class="h-8 w-8 rounded-lg bg-ruby-a2 text-ruby-11 flex items-center justify-center shrink-0 border border-ruby-a4"
              >
                <mat-icon fontIcon="factory" class="text-base leading-none shrink-0"></mat-icon>
              </div>
              <div>
                <h5 class="text-sm font-bold text-gray-12 m-0">Risques technologiques</h5>
                <p class="text-xs text-gray-11 m-0">Installations et activités industrielles</p>
              </div>
            </div>
            <span
              class="text-xs font-semibold px-2.5 py-1 rounded-full border shrink-0"
              [class.bg-amber-a2]="detectedTechnoRisks().length > 0"
              [class.text-amber-11]="detectedTechnoRisks().length > 0"
              [class.border-amber-a4]="detectedTechnoRisks().length > 0"
              [class.bg-jade-a2]="detectedTechnoRisks().length === 0"
              [class.text-jade-11]="detectedTechnoRisks().length === 0"
              [class.border-jade-a4]="detectedTechnoRisks().length === 0"
            >
              {{ technoRisksCountLabel() }}
            </span>
          </div>

          <div
            class="divide-y divide-gray-5/60 rounded-xl border border-gray-5/80 bg-gray-1/50 overflow-hidden"
          >
            @for (risk of technologicalRiskItems(); track risk.key) {
              <div
                class="flex items-center justify-between px-3.5 py-2.5 text-sm transition-colors"
                [class.bg-amber-a1/60]="risk.present"
                [class.hover:bg-gray-2]="!risk.present"
                [class.hover:bg-amber-a1]="risk.present"
              >
                <div class="flex items-center gap-3 min-w-0 pr-2">
                  <div
                    class="h-7 w-7 rounded-md flex items-center justify-center shrink-0"
                    [class.bg-amber-a2]="risk.present"
                    [class.text-amber-11]="risk.present"
                    [class.border]="risk.present"
                    [class.border-amber-a4]="risk.present"
                    [class.bg-gray-3]="!risk.present"
                    [class.text-gray-9]="!risk.present"
                  >
                    <mat-icon [fontIcon]="risk.icon" class="text-sm leading-none shrink-0"></mat-icon>
                  </div>
                  <span
                    class="text-sm"
                    [class.font-semibold]="risk.present"
                    [class.text-gray-12]="risk.present"
                    [class.text-gray-11]="!risk.present"
                  >
                    {{ risk.libelle }}
                  </span>
                </div>
                @if (risk.present) {
                  <span
                    class="inline-flex items-center gap-1 rounded-md bg-amber-a2 px-2.5 py-0.5 text-xs font-semibold text-amber-11 border border-amber-a4 shrink-0 shadow-2xs"
                  >
                    Présent
                  </span>
                } @else {
                  <span class="text-xs text-gray-9 shrink-0 flex items-center gap-1 font-medium">
                    Non exposé
                  </span>
                }
              </div>
            }
          </div>
        </div>
      </div>

      <!-- Official link bar -->
      <div
        class="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-gray-6 bg-gray-2/60 px-4 py-3 text-xs sm:text-sm text-gray-11"
      >
        <div class="flex items-center gap-2">
          <mat-icon fontIcon="info_outline" class="text-gray-10 text-base leading-none shrink-0"></mat-icon>
          <span class="m-0 text-xs sm:text-sm"
            >Données officielles issues du portail Géorisques du Gouvernement français.</span
          >
        </div>
        @if (data.url) {
          <a
            [href]="data.url"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1.5 font-semibold text-indigo-11 hover:underline shrink-0 text-xs sm:text-sm"
          >
            <span>Consulter le rapport officiel Géorisques</span>
            <mat-icon fontIcon="open_in_new" class="text-sm leading-none shrink-0"></mat-icon>
          </a>
        }
      </div>
    } @else {
      <!-- Empty / Pending State -->
      <div
        class="mt-4 flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-gray-6 py-8 text-center bg-gray-2/50"
      >
        <div class="h-10 w-10 rounded-full bg-gray-3 flex items-center justify-center text-gray-10">
          <mat-icon class="text-xl">sync_disabled</mat-icon>
        </div>
        <div>
          <p class="text-sm font-semibold text-gray-12 m-0">Synchronisation des risques en cours</p>
          <p class="text-sm text-gray-11 m-0 mt-1 max-w-sm">
            Les risques naturels et technologiques sont interrogés directement auprès du portail national
            Géorisques.
          </p>
        </div>
      </div>
    }
  </div>
</mat-expansion-panel>
